import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const ProfitMarginAnalysis = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="TrendingUp" className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Análisis de Margen de Ganancia
          </h3>
        </div>
        <div className="flex justify-center items-center h-64">
          <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  const { totalRevenue = 0, totalCost = 0, totalProfit = 0, profitMargin = 0, breakdown = [] } = data || {};

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(value);
  };

  const getMarginColor = (margin) => {
    if (margin >= 30) return 'text-green-600';
    if (margin >= 15) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getMarginBgColor = (margin) => {
    if (margin >= 30) return 'bg-green-100';
    if (margin >= 15) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const chartData = breakdown?.slice(0, 10)?.map(item => ({
    date: new Date(item?.date)?.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' }),
    revenue: item?.revenue,
    cost: item?.cost,
    profit: item?.profit,
    margin: item?.margin
  }))?.reverse();

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="TrendingUp" className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Análisis de Margen de Ganancia
        </h3>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" className="w-4 h-4 text-blue-600" />
            <p className="text-sm text-blue-600 font-medium">Ingresos</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {formatCurrency(totalRevenue)}
          </p>
        </div>

        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" className="w-4 h-4 text-orange-600" />
            <p className="text-sm text-orange-600 font-medium">Costos</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">
            {formatCurrency(totalCost)}
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-600 font-medium">Ganancia</p>
          </div>
          <p className="text-2xl font-bold text-green-700">
            {formatCurrency(totalProfit)}
          </p>
        </div>

        <div className={`${getMarginBgColor(profitMargin)} rounded-lg p-4`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Percent" className={`w-4 h-4 ${getMarginColor(profitMargin)}`} />
            <p className={`text-sm ${getMarginColor(profitMargin)} font-medium`}>Margen</p>
          </div>
          <p className={`text-2xl font-bold ${getMarginColor(profitMargin)}`}>
            {profitMargin?.toFixed(1)}%
          </p>
        </div>
      </div>

      {breakdown?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Icon name="TrendingUp" className="w-12 h-12 mb-3 opacity-50" />
          <p className="text-sm">No hay datos de margen de ganancia disponibles</p>
        </div>
      ) : (
        <>
          {/* Margin Trend Chart */}
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(value) => `${value?.toFixed(0)}%`}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Margen %') return `${value?.toFixed(1)}%`;
                    return formatCurrency(value);
                  }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Ingresos"
                  dot={{ fill: '#3b82f6', r: 4 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="cost"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Costos"
                  dot={{ fill: '#f59e0b', r: 4 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="profit"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Ganancia"
                  dot={{ fill: '#10b981', r: 4 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="margin"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Margen %"
                  dot={{ fill: '#8b5cf6', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Margin Performance Indicator */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">Rendimiento del Margen</p>
              <div className="flex items-center gap-2">
                <Icon
                  name={profitMargin >= 30 ? 'TrendingUp' : profitMargin >= 15 ? 'Minus' : 'TrendingDown'}
                  className={`w-4 h-4 ${getMarginColor(profitMargin)}`}
                />
                <span className={`text-sm font-semibold ${getMarginColor(profitMargin)}`}>
                  {profitMargin >= 30 ? 'Excelente' : profitMargin >= 15 ? 'Bueno' : 'Bajo'}
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  profitMargin >= 30 ? 'bg-green-600' : profitMargin >= 15 ? 'bg-yellow-600' : 'bg-red-600'
                }`}
                style={{ width: `${Math.min(profitMargin, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>0%</span>
              <span>15%</span>
              <span>30%</span>
              <span>50%+</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfitMarginAnalysis;